import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, serverTimestamp, updateDoc, collection } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { productService, orderService, db } from '../../services/firebaseService';
import { OrdersPage } from '../../features/orders';

export default function DemoUpdateModal({ isOpen = true, onClose, demo, onComplete }) {
  const navigate = useNavigate();
  const { updateDemoData, clearCart } = useCart();
  // Internal state to manage modal visibility
  const [isModalOpen, setIsModalOpen] = useState(isOpen);

  // Update internal state when isOpen prop changes
  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  // Handle closing the modal
  const handleClose = (e) => {
    e?.stopPropagation();
    setIsModalOpen(false);
    if (onClose) onClose(e);
  };

  const splitName = (fullName = '') => {
    const trimmed = fullName.trim();
    if (!trimmed) return { firstName: '', lastName: '' };

    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }

    const lastName = parts[parts.length - 1];
    const firstName = parts.slice(0, -1).join(' ');
    return { firstName, lastName };
  };
  const [formData, setFormData] = useState({
    customer: {
      firstName: '',
      lastName: '',
      name: '',
      email: '',
      mobile: ''
    },
    astCode: '',
    trnCode: '',
    bkpCode: '',
    hostDetails: {
      name: '',
      phone: ''
    },
    giftCode: '',
    giftName: '',
    outcome: 'considering',
    remarks: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [hostingGiftProducts, setHostingGiftProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const { currentAgent } = useAuth();

  // Upgrade verification state
  const [checkingUpgrade, setCheckingUpgrade] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [identityConfirmed, setIdentityConfirmed] = useState(false);

  const mapCountryFromIso = (countryIso) => {
    switch (countryIso) {
      case 'BHR':
        return 'Bahrain';
      case 'SAU':
        return 'Saudi Arabia';
      case 'ARE':
        return 'United Arab Emirates';
      case 'ZZZ':
      default:
        return 'Other';
    }
  };

  // Load hosting gift products on mount
  useEffect(() => {
    const loadHostingGiftProducts = async () => {
      try {
        setLoadingProducts(true);
        const allProducts = await productService.getProducts();
        
        // Filter products where Categories field contains "HstGft"
        const giftProducts = Object.values(allProducts).filter(
          product => product.Categories && product.Categories.includes('HstGft')
        );

        setHostingGiftProducts(giftProducts);
      } catch (error) {
        console.error('Error loading hosting gift products:', error);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadHostingGiftProducts();
  }, []);

  // Set up form data when demo changes
  useEffect(() => {
    if (demo?.payload) {
      const fullName = demo.payload.customer?.name || '';
      const { firstName, lastName } = splitName(fullName);

      setFormData({
        customer: {
          firstName,
          lastName,
          name: fullName,
          email: demo.payload.customer?.email || '',
          mobile: demo.payload.customer?.mobile || ''
        },
        astCode: demo.payload?.astCode || '',
        trnCode: demo.payload?.trnCode || '',
        bkpCode: demo.payload?.hostedDemo?.bkpCode || '',
        hostDetails: {
          name: demo.payload?.hostedDemo?.hostDetails?.name || '',
          phone: demo.payload?.hostedDemo?.hostDetails?.phone || ''
        },
        giftCode: demo.payload?.hostedDemo?.giftCode || '',
        giftName: demo.payload?.hostedDemo?.giftName || '',
        outcome: demo.payload?.outcome || 'considering',
        remarks: demo.payload.remarks || ''
      });
    }
  }, [demo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle nested customer object
    if (name.startsWith('customer.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        customer: {
          ...prev.customer,
          [field]: value
        }
      }));
    } else if (name.startsWith('hostDetails.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        hostDetails: {
          ...prev.hostDetails,
          [field]: value
        }
      }));
    } else if (name === 'giftCode') {
      // Auto-set giftName when giftCode changes
      const selectedProduct = hostingGiftProducts.find(p => p.id === value);
      setFormData(prev => ({
        ...prev,
        giftCode: value,
        giftName: selectedProduct?.name || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Reset upgrade state if outcome changes away from set_upgrade
    if (name === 'outcome' && value !== 'set_upgrade') {
      setUpgradeInfo(null);
      setIdentityConfirmed(false);
    }
  };

  const handleCheckUpgrade = async () => {
    try {
      setCheckingUpgrade(true);
      setError('');
      const result = await orderService.findCustomerUpgradeEligibility(
        formData.customer.email,
        formData.customer.mobile
      );
      setUpgradeInfo(result);
    } catch (err) {
      console.error('Error checking upgrade:', err);
      setError('Failed to verify upgrade eligibility.');
    } finally {
      setCheckingUpgrade(false);
    }
  };

  const validateMobileNumber = (mobile) => {
    const mobileRegex = /^\+[1-9]\d{1,14}$/;
    if (!mobile) return 'Mobile number is required';
    if (!mobileRegex.test(mobile)) return 'Please include valid country code (e.g., +97333123456)';
    if (mobile.length < 11) return 'Number too short (minimum 10 digits including country code)';
    if (mobile.length > 16) return 'Number too long (maximum 15 digits after +)';
    return null;
  };

  const validateCode = (code, fieldName) => {
    if (!code) return null; // Optional field
    const codeRegex = /^[A-Z]{3}$/;
    if (!codeRegex.test(code)) return `${fieldName} must be a valid 3-letter code (e.g., POP)`;
    return null;
  };

  // Add this function to check if submit should be enabled
  const isSubmitDisabled = () => {
    if (isSubmitting) return true;
    
    // For set_upgrade outcome, require upgrade eligibility AND identity confirmation
    if (formData.outcome === 'set_upgrade') {
      return !upgradeInfo?.eligible || !identityConfirmed;
    }
    
    // For other outcomes, just check basic required fields
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!demo) {
      setError('No demo data available');
      return;
    }

    // For set upgrade, we must have checked eligibility and confirmed identity
    if (formData.outcome === 'set_upgrade') {
      if (!upgradeInfo?.eligible) {
        setError('Please check upgrade eligibility before proceeding.');
        return;
      }
      if (!identityConfirmed) {
        setError('Please confirm the customer identity manually.');
        return;
      }
    }

    // Validate mobile number
    const mobileError = validateMobileNumber(formData.customer.mobile);
    if (mobileError) {
      setError(mobileError);
      return;
    }

    // Validate optional codes
    const astCodeError = validateCode(formData.astCode, 'Assistant Code');
    if (astCodeError) {
      setError(astCodeError);
      return;
    }

    const trnCodeError = validateCode(formData.trnCode, 'Training Code');
    if (trnCodeError) {
      setError(trnCodeError);
      return;
    }

    const bkpCodeError = validateCode(formData.bkpCode, 'Booking Partner Code');
    if (bkpCodeError) {
      setError(bkpCodeError);
      return;
    }

    // Validate mutual exclusivity: bkpCode and hostDetails cannot both exist
    if (formData.bkpCode && (formData.hostDetails.name || formData.hostDetails.phone)) {
      setError('Booking Partner Code and Host Details are mutually exclusive. Please specify only one.');
      return;
    }

    // Validate gift logic: mandatory when hostDetails present, optional when bkpCode present
    if ((formData.hostDetails.name || formData.hostDetails.phone) && !formData.giftCode) {
      setError('Hosting gift is required when host details are specified.');
      return;
    }

    // Get current date and time in Zulu (UTC) format
    const now = new Date();
    const formattedDate = now.toISOString().split('T')[0];
    const formattedTime = now.toISOString();

    try {
      setIsSubmitting(true);
      setError('');

      const demoRef = doc(db, 'demo_bookings', demo.id);
      const updateRef = doc(db, 'demo_update', demo.id);
      const timestamp = serverTimestamp();

      // Get current date and time in the required format
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toISOString();

      // Format the payload according to the required structure
      const payload = {
        agentCode: demo.payload?.agentCode || '',
        astCode: formData.astCode || '',
        trnCode: formData.trnCode || '',
        customer: {
          country_iso: demo.payload.customer?.country_iso || 'ARE',
          email: formData.customer.email || '',
          mobile: formData.customer.mobile || '',
          name: formData.customer.name || ''
        },
        dateTime: {
          demoDate: currentDate,
          timestamp: currentTime
        },
        demoId: demo.id,
        agentName: currentAgent?.name || demo.payload?.agentName || '',
        outcome: formData.outcome || 'considering',
        remarks: formData.remarks || '',
        completedAt: timestamp,
        createdAt: demo.payload?.createdAt || timestamp,
        hostedDemo: {
          isHosted: demo.payload?.hostedDemo?.isHosted || 'NO',
          bkpCode: formData.bkpCode || null,
          hostDetails: (formData.hostDetails.name || formData.hostDetails.phone) ? {
            name: formData.hostDetails.name,
            phone: formData.hostDetails.phone
          } : null,
          giftCode: formData.giftCode || null,
          giftName: formData.giftName || null
        }
      };

      // Only update status and outcome in demo_bookings
      const demoUpdate = {
        'payload.status': 'completed',
        'payload.outcome': formData.outcome || 'considering',
        'payload.updatedAt': timestamp
      };

      // Prepare minimal update for demo_update collection
      const updateData = {
        payload: {
          ...payload,
          upgradeInfo: formData.outcome === 'set_upgrade' ? upgradeInfo : null
        },
        updatedAt: timestamp
      };

      // Update demo_bookings with only status and outcome
      await updateDoc(demoRef, demoUpdate);

      // Create or update the demo update record with complete data
      await setDoc(updateRef, updateData, { merge: true });

      // Handle different outcomes
      if (formData.outcome === 'placed_order' || formData.outcome === 'set_upgrade') {
        updateDemoData({
          demoId: demo.id,
          customer: {
            name: formData.customer.name,
            email: formData.customer.email,
            mobile: formData.customer.mobile
          },
          referrer: demo.payload?.referrer || null,
          upgradeInfo: formData.outcome === 'set_upgrade' ? upgradeInfo : null
        });
        
        clearCart();
        setSuccess(true);
        if (onComplete) onComplete(formData.outcome);
        onClose();
        navigate('/orders');
      } else if (formData.outcome === 'did_not_purchase') {
        // Send WhatsApp message for did_not_purchase outcome
        try {
          const formattedPhone = formData.customer.mobile.replace(/\D/g, '');
          const cleanDemoId = (demo.id || '').replace(/-/g, '').toLowerCase();
          const feedbackUrl = `https://be.plsss.vip/feedback/${cleanDemoId}`;
          const messageRef = doc(collection(db, 'x_sendmsg'));

          await setDoc(messageRef, {
            msgbody: `Thank you for attending the demo. Please visit ${feedbackUrl} to help us improve our services.\n\nBest regards, *PLSSS Team*`,
            phoneno: formattedPhone,
            status: 'pending',
            type: 'whatsapp',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            metadata: {
              source: 'demo_update',
              demoId: demo?.id,
              createdBy: currentAgent?.id || currentAgent?.documentId,
              createdByEmail: currentAgent?.email
            }
          });

          // Also stage feedback data for this demo
          const feedbackStagingRef = doc(db, 'feedback_staging', demo.id);
          const today = new Date();
          const demoDate = today.toISOString().split('T')[0]; // YYYY-MM-DD

          const countryIso = demo.payload?.customer?.country_iso || '';
          const countryName = demo.payload?.customer?.country || mapCountryFromIso(countryIso);

          const fullName = formData.customer.name || demo.payload?.customer?.name || '';
          const { firstName, lastName } = splitName(fullName);
          const combinedName = `${firstName} ${lastName}`.trim() || fullName;

          await setDoc(feedbackStagingRef, {
            demoId: demo.id,
            customer: {
              firstName,
              lastName,
              name: combinedName,
              email: formData.customer.email || demo.payload?.customer?.email || '',
              mobile: formData.customer.mobile || demo.payload?.customer?.mobile || '',
              country: countryName,
              country_iso: countryIso
            },
            agentCode: demo.payload?.agentCode || currentAgent?.code || '',
            demoDate,
            status: 'pending'
          }, { merge: true });
        } catch (error) {
          console.error('Error queuing WhatsApp message or staging feedback:', error);
        }

        setSuccess(true);
        onClose();
        if (onComplete) onComplete(formData.outcome);
      } else {
        // For other outcomes, just close
        setSuccess(true);
        onClose();
        if (onComplete) onComplete(formData.outcome);
      }
    } catch (err) {
      console.error('Error updating demo:', err);
      setError('Failed to update demo. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render anything if not open
  if (!isModalOpen) return null;

  const inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    marginBottom: '1rem',
    fontSize: '0.875rem',
    lineHeight: '1.25rem'
  };

  const outcomeOptions = [
    { value: 'placed_order', label: 'Placed Order' },
    { value: 'set_upgrade', label: 'Set Upgrade' },
    { value: 'considering', label: 'Considering Purchase' },
    { value: 'did_not_purchase', label: 'Did Not Purchase' },
    { value: 'cancelled_demo', label: 'Demo Cancelled' }
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center',
      zIndex: 1000, padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white', padding: '25px', borderRadius: '8px',
        maxWidth: '800px', 
        width: '95%', maxHeight: '95vh',
        overflowY: 'auto', position: 'relative',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        <button onClick={onClose} disabled={isSubmitting} style={{
          position: 'absolute', top: '0.5rem', right: '0.5rem',
          background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer'
        }}>×</button>
        
        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>Demo Update</h2>
        
        {error && (
          <div style={{ padding: '1rem', backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: '0.375rem', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>First Name</label>
              <input type="text" name="customer.firstName" value={formData.customer.firstName} onChange={handleInputChange} style={inputStyle} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Last Name</label>
              <input type="text" name="customer.lastName" value={formData.customer.lastName} onChange={handleInputChange} style={inputStyle} required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Mobile *</label>
              <input type="tel" name="customer.mobile" value={formData.customer.mobile} onChange={handleInputChange} style={inputStyle} required placeholder="+97333123456" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Email</label>
              <input type="email" name="customer.email" value={formData.customer.email} onChange={handleInputChange} style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Demo Result *</label>
            <select name="outcome" value={formData.outcome} onChange={handleInputChange} style={inputStyle} required>
              {outcomeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {formData.outcome === 'set_upgrade' && (
            <div style={{ 
              backgroundColor: '#f5f3ff', padding: '15px', borderRadius: '12px', 
              border: '1px solid #ddd6fe', marginBottom: '20px' 
            }}>
              <h4 style={{ color: '#5b21b6', fontWeight: 'bold', marginBottom: '10px' }}>Set Upgrade Eligibility Check</h4>
              {!upgradeInfo ? (
                <button 
                  type="button" 
                  onClick={handleCheckUpgrade}
                  disabled={checkingUpgrade}
                  style={{
                    backgroundColor: '#7c3aed', color: 'white', padding: '10px 20px',
                    borderRadius: '8px', border: 'none', cursor: 'pointer'
                  }}
                >
                  {checkingUpgrade ? 'Searching...' : '🔍 Search Customer History'}
                </button>
              ) : (
                <div style={{ fontSize: '14px' }}>
                  {upgradeInfo.eligible ? (
                    <div style={{ color: '#1e40af' }}>
                      <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>✅ Eligible for Upgrade!</p>
                      <p>Customer: {upgradeInfo.customerName}</p>
                      <p>Owned Set: {upgradeInfo.highestSet.name} ({upgradeInfo.highestSet.sku})</p>
                      <p>Confidence Match: {upgradeInfo.confidence}%</p>
                      
                      <div style={{ marginTop: '15px', padding: '10px', backgroundColor: 'white', borderRadius: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input 
                            type="checkbox" 
                            checked={identityConfirmed} 
                            onChange={(e) => setIdentityConfirmed(e.target.checked)}
                            style={{ marginRight: '10px', width: '18px', height: '18px' }}
                          />
                          <span style={{ fontWeight: 'bold' }}>I confirm that this is the same customer.</span>
                        </label>
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: '#991b1b' }}>❌ {upgradeInfo.message}</p>
                  )}
                  <button 
                    type="button" 
                    onClick={() => setUpgradeInfo(null)}
                    style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Reset Search
                  </button>
                </div>
              )}
              {/* Helper text for upgrade requirements */}
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280' }}>
                {!upgradeInfo && (
                  <span>🔍 Please search customer history first.</span>
                )}
                {upgradeInfo && !upgradeInfo.eligible && (
                  <span style={{ color: '#dc2626' }}>⚠️ Customer is not eligible for upgrade. Please select a different outcome.</span>
                )}
                {upgradeInfo?.eligible && !identityConfirmed && (
                  <span style={{ color: '#f59e0b' }}>⚠️ Please confirm customer identity to enable update.</span>
                )}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Remarks</label>
            <textarea name="remarks" value={formData.remarks} onChange={handleInputChange} style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', cursor: 'pointer' }}>
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitDisabled()}
              style={{ 
                padding: '10px 20px', 
                borderRadius: '8px', 
                border: 'none', 
                backgroundColor: isSubmitDisabled() ? '#9CA3AF' : '#033EA5',
                color: 'white', 
                fontWeight: 'bold',
                cursor: isSubmitDisabled() ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Updating...' : 'Save Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
